import type { I18NManager } from "../../../shared/core/i18n/i18n-manager";
import { logger } from "../../../shared/core/logging/logger";
import type { AuthenticationManager } from "../../../shared/domains/authentication/authentication-manager";
import type { ClientManager } from "../../../shared/domains/client/client-manager";
import { CONFIGURATION_MEDIA } from "../../../shared/domains/configuration/configuration";
import type { ConfigurationManager } from "../../../shared/domains/configuration/configuration-manager";
import { Observable } from "../../../shared/utils/observable";
import { updateTheme } from "../../ui/styles/theme";
import type { SessionStorage } from "../cache/session-storage";

const HAS_PROMPT_PINCODE_CHANGED_KEY = "has_prompt_pincode_changed";
export class WebAppStartManager {
  public constructor(
    private configurationManager: ConfigurationManager,
    private i18nManager: I18NManager,
    private authenticationManager: AuthenticationManager,
    private clientManager: ClientManager,
    private sessionStorage: SessionStorage<boolean>,
  ) {
    this.addLoginTasks();
    this.notifyWhenClientMustChangePincode();
    this.notifyLocaleChange();
  }

  shouldPromptPincodeChange = new Observable(false);

  async startApp() {
    await this.initServices();
  }

  private async initServices() {
    try {
      await Promise.all([
        this.authenticationManager.initialize(),
        this.configurationManager.load(CONFIGURATION_MEDIA.WEB_CLIENT),
      ]);
    } catch (e) {
      await this.i18nManager.initialize(null);
      await this.authenticationManager.logout();
      logger.error("Error while initializing services", e);
      // window.location.reload();
    }
    updateTheme();
    await this.i18nManager.initialize(this.clientManager.client.get());
  }

  private addLoginTasks() {
    // The signal fires on both edges. There is no holder to fetch on the way out, and the request
    // would be turned away by the connection check, rejecting with nobody left to hear it.
    this.authenticationManager.isConnected.onChange.add((isConnected) => {
      if (!isConnected) {
        return;
      }
      this.clientManager
        .updateClient(true)
        .catch((e) => logger.debug("WebAppStartManager", "Failed to update the client on login", e));
    });
  }

  private notifyWhenClientMustChangePincode() {
    this.clientManager.client.onChange.add(async (client) => {
      if (client?.mustChangePincode) {
        const hasAlreadyPromptPincodeChange = await this.sessionStorage.read(HAS_PROMPT_PINCODE_CHANGED_KEY);
        if (!hasAlreadyPromptPincodeChange) {
          this.shouldPromptPincodeChange.set(true);
        }
      }
    });
  }

  private notifyLocaleChange() {
    this.clientManager.client.onChange.add(async (client) => {
      const currentLocale = this.i18nManager.localeTag.get();
      if (client?.lang !== currentLocale) {
        this.i18nManager.initialize(client);
      }
    });
  }

  hasPromptPincodeChange() {
    this.shouldPromptPincodeChange.set(false);
    this.sessionStorage.store(true, HAS_PROMPT_PINCODE_CHANGED_KEY);
  }
}
